import { FC, useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckoutView } from '@/app/_checkout';
import type { CheckoutCopy, CheckoutData, OrderSummaryModel } from '@/app/_checkout';
import { useInitializePaymentService } from './services/use-initialize-payment-service';
import { usePayService } from './services/use-pay-service';

const ServiceCallPage: FC = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation(['service-call']);

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const authenticateUrl = searchParams.get('authenticate_url') ?? '';
  const lang = searchParams.get('lang') ?? '';

  const { message = '' } = useParams<{ message: string }>();
  const { data, isLoading, error } = useInitializePaymentService(message, authenticateUrl);
  const payMutation = usePayService(message);

  useEffect(() => {
    if (lang) i18n.changeLanguage(lang);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data?.meta?.jwt_token) {
      sessionStorage.setItem('jwt_token', data.meta.jwt_token);
    }
  }, [data?.meta?.jwt_token]);

  const serviceCall = data?.meta.service_call;

  const checkoutData = useMemo<CheckoutData | undefined>(() => {
    if (!data || !serviceCall) return undefined;
    return {
      gatewayConfig: data.meta.gateway_config,
      paymentMethods: data.meta.payment_methods,
      billingInfo: {
        first_name: data.attributes.first_name,
        last_name: data.attributes.last_name,
        email: data.attributes.email,
        country: 'CA',
        address: serviceCall.service_address,
      },
      contractId: serviceCall.contract_id,
      paymentAvailable: serviceCall.payment_installments_status,
      company: {
        name: serviceCall.company_name,
        email: serviceCall.company_email,
        phone: serviceCall.company_phone,
        logo: serviceCall.company_logo,
      },
      invalidateKey: ['service-call-initialize', message],
    };
  }, [data, serviceCall, message]);

  const summary = useMemo<OrderSummaryModel | undefined>(() => {
    if (!serviceCall) return undefined;
    const taxes = serviceCall.taxes ?? [];
    const taxTotal = taxes.reduce((sum, tax) => sum + tax.amount, 0);
    return {
      lines: [{ label: serviceCall.title, sublabel: serviceCall.offered_service }],
      subtotal: serviceCall.price,
      taxes,
      total: serviceCall.price + taxTotal,
    };
  }, [serviceCall]);

  const company = serviceCall?.company_name ?? '';
  const copy = useMemo<CheckoutCopy>(() => ({
    successTitle: t('service-call:success-title', 'Payment Successful!'),
    successDescription: t(
      'service-call:success-description',
      'Thank you for your payment. Your service call has been confirmed.',
    ),
    alreadyPaidTitle: t('service-call:already-paid-title', 'Already Paid'),
    alreadyPaidDescription: t(
      'service-call:already-paid-description',
      'This service call has already been paid.',
    ),
    unavailableSubtitle: t(
      'service-call:payment-unavailable-subtitle',
      "{{company}} Doesn't Accept Online Payments",
      { company },
    ),
    unavailableDescription: t(
      'service-call:payment-unavailable-description',
      'To arrange payment for your service call, contact {{company}} directly using the options below.',
      { company },
    ),
    unavailableReference: t(
      'service-call:payment-unavailable-reference',
      'Please mention this service call when reaching out so they can assist you faster.',
    ),
  }), [t, company]);

  return (
    <CheckoutView
      isLoading={isLoading}
      error={error}
      data={checkoutData}
      summary={summary}
      payMutation={payMutation}
      copy={copy}
    />
  );
};

export default ServiceCallPage;
